import React, { useState, useEffect } from 'react';
import {
    Box,
    useMediaQuery,
    IconButton,
    useTheme,
    Fade
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

const CustomCarousel = ({ banners, setBanners, endpointsUrl }) => {

    const theme = useTheme();
    const isMobile = useMediaQuery("(max-width:768px)");
    const isTablet = useMediaQuery("(max-width:1024px)");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (banners.length === 0 || !isPlaying) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex === banners.length - 2 ? 0 : prevIndex + 1));
        }, 4000); // Increased to 4 seconds for better UX
        return () => clearInterval(interval);
    }, [banners, isPlaying]);

    useEffect(() => {
        if (currentIndex === banners.length - 2) {
            setBanners([...banners]);
        }
    }, [currentIndex, banners, setBanners]);

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? banners.length - 2 : prevIndex - 1));
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex === banners.length - 2 ? 0 : prevIndex + 1));
    };

    const togglePlayPause = () => {
        setIsPlaying(!isPlaying);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: { xs: 200, sm: 280, md: 380, lg: 450 },
                overflow: 'visible', // Changed to visible to allow arrows outside
                borderRadius: { xs: 2, md: 3 },
                display: 'flex',
                alignItems: 'center',
                '&:hover .carousel-controls': {
                    opacity: 1,
                },
                '&:hover .carousel-play-pause': {
                    opacity: 1,
                }
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Left Arrow - Outside the image */}
            {!isMobile && (
                <IconButton
                    className="carousel-controls"
                    onClick={goToPrevious}
                    sx={{
                        position: 'absolute',
                        left: -60, // Positioned outside the container
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        color: '#1354C1',
                        opacity: 0,
                        transition: 'all 0.3s ease',
                        zIndex: 3,
                        width: 48,
                        height: 48,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                            bgcolor: '#fff',
                            opacity: 1,
                            transform: 'translateY(-50%) scale(1.1)',
                            boxShadow: '0 6px 20px rgba(19, 84, 193, 0.3)',
                        },
                    }}
                >
                    <ArrowBackIosIcon sx={{ fontSize: '24px' }} />
                </IconButton>
            )}

            {/* Main Carousel Container - with padding for arrows */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    borderRadius: { xs: 2, md: 3 },
                    mx: { xs: 0, md: 0 }, // No margin needed as arrows are outside
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: `translateX(${-currentIndex * 100}%)`,
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {banners.map((banner, index) => (
                        <Box
                            key={index}
                            sx={{
                                flex: '0 0 100%',
                                position: 'relative',
                                height: '100%',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: banner?.contentLink ? 'pointer' : 'default',
                            }}
                            onClick={() => {
                                if (banner?.contentLink) {
                                    window.open(banner.contentLink, '_blank', 'noreferrer');
                                }
                            }}
                        >
                            <img
                                src={endpointsUrl + banner?.banner}
                                alt={`Banner ${index + 1}`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    // objectFit: 'cover',
                                    objectPosition: 'center',
                                    display: 'block',
                                    transition: 'transform 0.3s ease',
                                }}
                                onLoad={(e) => {
                                    e.target.style.opacity = '1';
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    console.error('Failed to load banner image:', banner?.banner);
                                }}
                            />

                            {/* Subtle Gradient Overlay */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.1) 100%)',
                                    opacity: isHovered ? 0.3 : 0.1,
                                    transition: 'opacity 0.3s ease',
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Right Arrow - Outside the image */}
            {!isMobile && (
                <IconButton
                    className="carousel-controls"
                    onClick={goToNext}
                    sx={{
                        position: 'absolute',
                        right: -60, // Positioned outside the container
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        color: '#1354C1',
                        opacity: 0,
                        transition: 'all 0.3s ease',
                        zIndex: 3,
                        width: 48,
                        height: 48,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                            bgcolor: '#fff',
                            opacity: 1,
                            transform: 'translateY(-50%) scale(1.1)',
                            boxShadow: '0 6px 20px rgba(19, 84, 193, 0.3)',
                        },
                    }}
                >
                    <ArrowForwardIosIcon sx={{ fontSize: '24px' }} />
                </IconButton>
            )}

            {/* Mobile Navigation Arrows - Inside but at edges */}
            {isMobile && (
                <>
                    <IconButton
                        onClick={goToPrevious}
                        sx={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            color: '#1354C1',
                            opacity: 0.8,
                            transition: 'all 0.3s ease',
                            zIndex: 3,
                            width: 36,
                            height: 36,
                            '&:hover': {
                                bgcolor: '#fff',
                                opacity: 1,
                                transform: 'translateY(-50%) scale(1.1)',
                            },
                        }}
                    >
                        <ArrowBackIosIcon sx={{ fontSize: '18px' }} />
                    </IconButton>

                    <IconButton
                        onClick={goToNext}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            color: '#1354C1',
                            opacity: 0.8,
                            transition: 'all 0.3s ease',
                            zIndex: 3,
                            width: 36,
                            height: 36,
                            '&:hover': {
                                bgcolor: '#fff',
                                opacity: 1,
                                transform: 'translateY(-50%) scale(1.1)',
                            },
                        }}
                    >
                        <ArrowForwardIosIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                </>
            )}

            {/* Play/Pause Button */}
            <IconButton
                className="carousel-play-pause"
                onClick={togglePlayPause}
                sx={{
                    position: 'absolute',
                    top: { xs: 8, md: 16 },
                    right: { xs: 8, md: 16 },
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    color: '#1354C1',
                    opacity: { xs: 0.6, md: 0 },
                    transition: 'all 0.3s ease',
                    zIndex: 3,
                    width: { xs: 32, md: 40 },
                    height: { xs: 32, md: 40 },
                    '&:hover': {
                        bgcolor: '#fff',
                        opacity: 1,
                        transform: 'scale(1.1)',
                    },
                }}
            >
                {isPlaying ?
                    <PauseIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} /> :
                    <PlayArrowIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
                }
            </IconButton>

            {/* Dots Indicator */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1,
                    zIndex: 3,
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '20px',
                    padding: '8px 12px',
                    backdropFilter: 'blur(10px)',
                }}
            >
                {banners.slice(0, -1).map((_, index) => (
                    <Box
                        key={index}
                        onClick={() => goToSlide(index)}
                        sx={{
                            width: { xs: 10, md: 12 },
                            height: { xs: 10, md: 12 },
                            borderRadius: '50%',
                            bgcolor: index === currentIndex ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            border: index === currentIndex ? '2px solid #1354C1' : '2px solid transparent',
                            transform: index === currentIndex ? 'scale(1.2)' : 'scale(1)',
                            boxShadow: index === currentIndex ? '0 2px 8px rgba(19, 84, 193, 0.4)' : 'none',
                            '&:hover': {
                                transform: 'scale(1.3)',
                                bgcolor: '#fff',
                            },
                        }}
                    />
                ))}
            </Box>

            {/* Progress Bar */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    zIndex: 2,
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        bgcolor: '#1354C1',
                        background: 'linear-gradient(90deg, #1354C1 0%, #0d47a1 100%)',
                        width: `${((currentIndex + 1) / (banners.length - 1)) * 100}%`,
                        transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
            </Box>
        </Box>
    );
};

export default CustomCarousel;
